import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import PlaceSearch from '../components/PlaceSearch';
import GoogleMap from '../components/GoogleMap';
import { useAuth } from '../contexts/AuthContext';
import { getTenant, updateTenant, Tenant, uploadFile } from '../services/api';

interface TenantSettingsProps {
  skipLayout?: boolean;
}

const TenantSettings: React.FC<TenantSettingsProps> = ({ skipLayout = false }) => {
  const { user, canModifyTenantSettings } = useAuth();
  const isSystemAdmin = user?.isSystem;
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMpin, setShowMpin] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    code: '',
    email: '',
    phone: '',
    password: '',
    mPin: '',
    hospitalName: '',
    hospitalLocation: '',
    hospitalEmail: '',
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [isDevMode, setIsDevMode] = useState(false);

  // Check URL hash for #dev to allow editing Group ID
  useEffect(() => {
    const checkHash = () => {
      setIsDevMode(window.location.hash === '#dev');
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    domain: '',
    email: '',
    phone: '',
    password: '',
    mPin: '',
    address: '',
    description: '',
    groupId: '',
    hospital: {
      name: '',
      logo: '',
      addressDetails: '',
      addressFormatted: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: '',
      email: '',
      website: '',
      registrationNumber: '',
      licenseNumber: '',
      emergencyContact: '',
      emergencyPhone: '',
      latitude: undefined as number | undefined,
      longitude: undefined as number | undefined,
    },
  });

  const [hospitalLocation, setHospitalLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    code: '',
    domain: '',
    email: '',
    phone: '',
    password: '',
    mPin: '',
    address: '',
    description: '',
    groupId: '',
    hospital: {
      name: '',
      logo: '',
      addressDetails: '',
      addressFormatted: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: '',
      email: '',
      website: '',
      registrationNumber: '',
      licenseNumber: '',
      emergencyContact: '',
      emergencyPhone: '',
      latitude: undefined as number | undefined,
      longitude: undefined as number | undefined,
    },
  });
  const [originalHospitalLocation, setOriginalHospitalLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoUploadError('Please select an image file');
      return;
    }
    const maxBytes = 1.5 * 1024 * 1024;
    if (file.size > maxBytes) {
      const { formatFileSizeMB } = await import('../utils/imageCompression');
      setLogoUploadError(
        `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (1.5 MB). Please choose a smaller file.`
      );
      return;
    }

    setLogoUploadError('');
    setLogoUploading(true);
    const run = async () => {
      try {
        const { compressImage } = await import('../utils/imageCompression');
        const compressedFile = await compressImage(file, 800, 800, 0.8, 300);

        const formData = new FormData();
        formData.append('file', compressedFile);
        formData.append('folderPath', 'images/hospital-logos');
        const response = await uploadFile(formData);
        const url = response.data?.data?.url;
        if (url) {
          setFormData((prev) => ({
            ...prev,
            hospital: { ...prev.hospital, logo: url },
          }));
        } else {
          setLogoUploadError('Upload succeeded but no URL returned.');
        }
      } catch (err: any) {
        setLogoUploadError(err.response?.data?.message || err.message || 'Failed to upload logo');
      } finally {
        setLogoUploading(false);
        if (e.target) e.target.value = '';
      }
    };
    setTimeout(run, 0);
  };

  const handleClearLogo = () => {
    setFormData((prev) => ({
      ...prev,
      hospital: { ...prev.hospital, logo: '' },
    }));
    setLogoUploadError('');
  };

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user?.tenantId) {
        setError('No tenant associated with your account');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const tenantData = response.data.data;
          setTenant(tenantData);
          const hospital = tenantData.spec.hospital;
          const hasLocation = hospital?.latitude !== undefined && hospital?.longitude !== undefined;

          const initialFormData = {
            name: tenantData.metadata.name,
            code: tenantData.metadata.code || '',
            domain: tenantData.spec.domain || '',
            email: tenantData.spec.email || '',
            phone: tenantData.spec.phone || '',
            password: '',
            mPin: '',
            address: tenantData.spec.address || '',
            description: tenantData.spec.description || '',
            groupId: tenantData.spec.groupId || '',
            hospital: {
              name: hospital?.name || '',
              logo: hospital?.logo || '',
              addressDetails: '', // cannot reliably split; leave empty for edits
              addressFormatted: hospital?.address || '',
              city: hospital?.city || '',
              state: hospital?.state || '',
              zipCode: hospital?.zipCode || '',
              country: hospital?.country || '',
              phone: hospital?.phone || '',
              email: hospital?.email || '',
              website: hospital?.website || '',
              registrationNumber: hospital?.registrationNumber || '',
              licenseNumber: hospital?.licenseNumber || '',
              emergencyContact: hospital?.emergencyContact || '',
              emergencyPhone: hospital?.emergencyPhone || '',
              latitude: hospital?.latitude,
              longitude: hospital?.longitude,
            },
          };
          setFormData(initialFormData);
          setOriginalFormData(initialFormData);

          // Set hospital location for map display
          if (hasLocation) {
            const locationData = {
              latitude: hospital!.latitude!,
              longitude: hospital!.longitude!,
            };
            setHospitalLocation(locationData);
            setOriginalHospitalLocation(locationData);
          } else if (hospital?.address || hospital?.city || hospital?.state) {
            // Try to geocode existing address after formData is set
            setTimeout(() => {
              const addressParts = [
                hospital?.name,
                hospital?.address,
                hospital?.city,
                hospital?.state,
                hospital?.zipCode,
                hospital?.country,
              ].filter(Boolean);

              if (addressParts.length > 0 && window.google && window.google.maps) {
                const address = addressParts.join(', ');
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: address }, (results, status) => {
                  if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
                    const location = results[0].geometry.location;
                    const locationData = {
                      latitude: location.lat(),
                      longitude: location.lng(),
                      address: results[0].formatted_address || address,
                    };
                    setHospitalLocation(locationData);
                    setFormData((prev) => ({
                      ...prev,
                      hospital: {
                        ...prev.hospital,
                        latitude: locationData.latitude,
                        longitude: locationData.longitude,
                      },
                    }));
                  }
                });
              }
            }, 500);
          }
        }
      } catch (err: any) {
        setError('Failed to load tenant details');
        console.error('Failed to load tenant:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [user?.tenantId]);

  type ReverseGeocodeResult = {
    formattedAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    if (!window.google || !window.google.maps) return null;
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          resolve(results[0].formatted_address || null);
        } else {
          resolve(null);
        }
      });
    });
  };

  const reverseGeocodeWithComponents = async (lat: number, lng: number): Promise<ReverseGeocodeResult | null> => {
    if (!window.google || !window.google.maps) return null;
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          resolve(null);
          return;
        }
        const result = results[0] as google.maps.GeocoderResult & { address_components?: Array<{ types: string[]; long_name: string; short_name: string }> };
        const components = result.address_components || [];
        const get = (type: string) => components.find((c: { types: string[]; long_name: string }) => c.types.includes(type))?.long_name || '';
        const getShort = (type: string) => components.find((c: { types: string[]; short_name: string }) => c.types.includes(type))?.short_name || '';
        resolve({
          formattedAddress: result.formatted_address || '',
          city: get('locality') || get('sublocality') || get('administrative_area_level_2'),
          state: get('administrative_area_level_1') || getShort('administrative_area_level_1'),
          zipCode: get('postal_code'),
          country: get('country') || getShort('country'),
        });
      });
    });
  };

  // Handle location change from map or PlaceSearch
  const handleLocationChange = async (lat: number, lng: number, address?: string) => {
    const components = await reverseGeocodeWithComponents(lat, lng);
    const resolvedAddress =
      address || (components?.formattedAddress) || (await reverseGeocode(lat, lng)) || formData.hospital.addressFormatted || '';
    const location = { latitude: lat, longitude: lng, address: resolvedAddress };
    setHospitalLocation(location);
    setFormData({
      ...formData,
      hospital: {
        ...formData.hospital,
        latitude: lat,
        longitude: lng,
        addressFormatted: resolvedAddress,
        city: components?.city ?? formData.hospital.city,
        state: components?.state ?? formData.hospital.state,
        zipCode: components?.zipCode ?? formData.hospital.zipCode,
        country: components?.country ?? formData.hospital.country,
      },
    });
    setValidationErrors((prev) => ({
      ...prev,
      hospitalLocation: '',
    }));
  };

  const handleEdit = () => {
    if (!canModifyTenantSettings) return;
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form data to original
    setFormData({ ...originalFormData });
    setHospitalLocation(originalHospitalLocation ? { ...originalHospitalLocation } : null);
    setValidationErrors({
      code: '',
      email: '',
      phone: '',
      password: '',
      mPin: '',
      hospitalName: '',
      hospitalLocation: '',
      hospitalEmail: '',
    });
    setLogoUploadError('');
    setShowPassword(false);
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    const compareHospital = (h1: typeof formData.hospital, h2: typeof originalFormData.hospital) => {
      return (
        h1.name.trim() !== h2.name.trim() ||
        h1.logo !== h2.logo ||
        h1.addressFormatted !== h2.addressFormatted ||
        h1.city.trim() !== h2.city.trim() ||
        h1.state.trim() !== h2.state.trim() ||
        h1.zipCode.trim() !== h2.zipCode.trim() ||
        h1.country.trim() !== h2.country.trim() ||
        h1.phone.trim() !== h2.phone.trim() ||
        h1.email.trim() !== h2.email.trim() ||
        h1.website.trim() !== h2.website.trim() ||
        h1.registrationNumber.trim() !== h2.registrationNumber.trim() ||
        h1.licenseNumber.trim() !== h2.licenseNumber.trim() ||
        h1.emergencyContact.trim() !== h2.emergencyContact.trim() ||
        h1.emergencyPhone.trim() !== h2.emergencyPhone.trim() ||
        h1.latitude !== h2.latitude ||
        h1.longitude !== h2.longitude
      );
    };

    const locationChanged =
      (hospitalLocation?.latitude !== originalHospitalLocation?.latitude) ||
      (hospitalLocation?.longitude !== originalHospitalLocation?.longitude);

    return (
      formData.name.trim() !== originalFormData.name.trim() ||
      formData.code.trim() !== originalFormData.code.trim() ||
      formData.domain.trim() !== originalFormData.domain.trim() ||
      formData.email.trim() !== originalFormData.email.trim() ||
      formData.phone.trim() !== originalFormData.phone.trim() ||
      formData.password !== '' || // Password is only set when changing
      formData.mPin.trim() !== originalFormData.mPin.trim() ||
      formData.address.trim() !== originalFormData.address.trim() ||
      formData.description.trim() !== originalFormData.description.trim() ||
      formData.groupId.trim() !== originalFormData.groupId.trim() ||
      compareHospital(formData.hospital, originalFormData.hospital) ||
      locationChanged
    );
  };

  const validateCode = (code: string): string => {
    if (!code) return 'Tenant Code is required';
    const codeRegex = /^[A-Z0-9]+$/;
    if (!codeRegex.test(code.toUpperCase())) {
      return 'Code must contain only uppercase letters and numbers';
    }
    const codeUpper = code.toUpperCase();
    if (codeUpper === 'AMS' || codeUpper === 'AMBULANCE') {
      return `Tenant Code "${codeUpper}" is reserved and cannot be used`;
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return '';
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return ''; // Password is optional for updates
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const validateMPin = (value: string): string => {
    if (!value) return ''; // Optional for tenant; empty means "no MPIN"
    if (!/^[0-9]{4,6}$/.test(value.trim())) {
      return 'MPIN must be 4 to 6 digits (numbers only)';
    }
    return '';
  };

  // Capitalize the first letter of each word in a name
  const capitalizeName = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModifyTenantSettings) return;
    if (saving) return;
    setError('');
    setSuccess(false);

    // Validate fields
    const codeError = validateCode(formData.code);
    let emailError = validateEmail(formData.email);
    let phoneError = validatePhone(formData.phone);
    const passwordError = validatePassword(formData.password);
    const mPinError = validateMPin(formData.mPin);
    const hospitalEmailError = validateEmail(formData.hospital.email);
    const hospitalNameError = formData.hospital.name.trim() ? '' : 'Hospital name is required';
    const hospitalLocationError =
      hospitalLocation && hospitalLocation.latitude && hospitalLocation.longitude
        ? ''
        : 'Hospital location is required';

    // Require at least one of email or phone
    if (!formData.email && !formData.phone) {
      const atLeastOneMessage = 'Please provide at least email or phone number';
      emailError = atLeastOneMessage;
      phoneError = atLeastOneMessage;
      setError(atLeastOneMessage);
    }

    setValidationErrors({
      code: codeError,
      email: emailError,
      phone: phoneError,
      password: passwordError,
      mPin: mPinError,
      hospitalName: hospitalNameError,
      hospitalLocation: hospitalLocationError,
      hospitalEmail: hospitalEmailError,
    });

    if (
      codeError ||
      emailError ||
      phoneError ||
      passwordError ||
      mPinError ||
      hospitalNameError ||
      hospitalLocationError ||
      hospitalEmailError
    ) {
      return;
    }

    if (!tenant) {
      setError('Tenant not loaded');
      return;
    }

    setSaving(true);
    try {

      // Build hospital object only if at least one field is filled
      const hospitalData: any = {};
      const hospitalDetails = formData.hospital.addressDetails?.trim() || '';
      const hospitalGeoAddress = formData.hospital.addressFormatted?.trim() || '';
      const hospitalAddressCombined = [hospitalDetails, hospitalGeoAddress].filter(Boolean).join(' | ');
      const hasHospitalFields =
        formData.hospital.name ||
        formData.hospital.logo ||
        hospitalAddressCombined ||
        formData.hospital.city ||
        formData.hospital.state ||
        formData.hospital.zipCode ||
        formData.hospital.country ||
        formData.hospital.phone ||
        formData.hospital.email ||
        formData.hospital.website ||
        formData.hospital.registrationNumber ||
        formData.hospital.licenseNumber ||
        formData.hospital.emergencyContact ||
        formData.hospital.emergencyPhone ||
        formData.hospital.latitude !== undefined ||
        formData.hospital.longitude !== undefined;

      if (hasHospitalFields) {
        if (formData.hospital.name) hospitalData.name = capitalizeName(formData.hospital.name);
        if (formData.hospital.logo) hospitalData.logo = formData.hospital.logo;
        if (hospitalAddressCombined) hospitalData.address = hospitalAddressCombined;
        if (formData.hospital.city) hospitalData.city = formData.hospital.city;
        if (formData.hospital.state) hospitalData.state = formData.hospital.state;
        if (formData.hospital.zipCode) hospitalData.zipCode = formData.hospital.zipCode;
        if (formData.hospital.country) hospitalData.country = formData.hospital.country;
        if (formData.hospital.phone) hospitalData.phone = formData.hospital.phone;
        if (formData.hospital.email) hospitalData.email = formData.hospital.email;
        if (formData.hospital.website) hospitalData.website = formData.hospital.website;
        if (formData.hospital.registrationNumber)
          hospitalData.registrationNumber = formData.hospital.registrationNumber;
        if (formData.hospital.licenseNumber) hospitalData.licenseNumber = formData.hospital.licenseNumber;
        if (formData.hospital.emergencyContact) hospitalData.emergencyContact = formData.hospital.emergencyContact;
        if (formData.hospital.emergencyPhone) hospitalData.emergencyPhone = formData.hospital.emergencyPhone;
        if (formData.hospital.latitude !== undefined) hospitalData.latitude = formData.hospital.latitude;
        if (formData.hospital.longitude !== undefined) hospitalData.longitude = formData.hospital.longitude;
      }

      const data: any = {
        metadata: {
          name: capitalizeName(formData.name),
          code: formData.code.trim().toUpperCase(),
        },
        spec: {
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          description: formData.description,
          mPin: formData.mPin ? formData.mPin.trim() : undefined,
          domain: (() => {
            const raw = formData.domain.trim();
            if (!raw) return '';
            return raw.toLowerCase().replace(/^https?:\/\//i, '').split('/')[0].replace(/\/+$/, '').trim();
          })(),
          ...(formData.groupId && { groupId: formData.groupId }),
          hospital: Object.keys(hospitalData).length > 0 ? hospitalData : undefined,
        },
      };

      // Only include password if provided
      if (formData.password) {
        data.spec.password = formData.password;
      }

      const response = await updateTenant(tenant._id, data);

      if (response.data.success) {
        setSuccess(true);
        // Update local tenant state
        setTenant(response.data.data);
        // Clear password field
        const updatedFormData = { ...formData, password: '' };
        setFormData(updatedFormData);
        // Update original form data to current state after successful save
        setOriginalFormData(updatedFormData);
        setOriginalHospitalLocation(hospitalLocation ? { ...hospitalLocation } : null);
        // Exit edit mode after successful save
        setIsEditMode(false);
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update tenant details';
      setError(errorMessage);
      console.error('Failed to update tenant:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const loadingContent = <div className="loading">Loading tenant details...</div>;
    if (skipLayout) return loadingContent;
    return <DashboardLayout>{loadingContent}</DashboardLayout>;
  }

  if (!tenant) {
    const errorContent = <div className="error-message">{error || 'Tenant not found'}</div>;
    if (skipLayout) return errorContent;
    return <DashboardLayout>{errorContent}</DashboardLayout>;
  }

  const content = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 270px)' }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0 }}>
          {/* <div className="page-header">
            <h2>Tenant Settings</h2>
            <p>Manage your tenant and hospital details</p>
          </div> */}

          {error && <div className="error-message">{error}</div>}
          {success && (
            <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '6px', marginBottom: '20px' }}>
              Tenant details updated successfully!
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: '1', overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="card" style={{ flex: '1', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: '1', minHeight: 0 }}>
              <div style={{ flex: '1', overflowY: 'auto', minHeight: 0 }}>
                {/* <h3 style={{ marginBottom: '24px' }}>Tenant Information</h3> */}

                <div className="form-group">
                  <label>
                    Tenant Name <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Tenant Code <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    placeholder="Enter tenant code"
                    onChange={(e) => {
                      // Only allow uppercase letters and numbers, convert to uppercase
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      setFormData({ ...formData, code: value });
                      setValidationErrors({ ...validationErrors, code: '' });
                    }}
                    style={{ textTransform: 'uppercase' }}
                    className={validationErrors.code ? 'input-error' : ''}
                    required
                    disabled={!isSystemAdmin || !isEditMode}
                  />
                  {validationErrors.code && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.code}
                    </small>
                  )}
                  {/* {!validationErrors.code && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Only uppercase letters and numbers allowed (e.g., ABC123)
                    </small>
                  )} */}
                </div>

                {isDevMode && (
                  <div className="form-group">
                    <label>Domain</label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => {
                        const raw = e.target.value.toLowerCase();
                        const noSpaces = raw.replace(/\s+/g, '');
                        setFormData({ ...formData, domain: noSpaces });
                      }}
                      onBlur={() => {
                        const raw = formData.domain.trim();
                        if (!raw) return;
                        const normalized = raw.toLowerCase().replace(/^https?:\/\//i, '').split('/')[0].replace(/\/+$/, '').trim();
                        if (normalized !== raw) setFormData({ ...formData, domain: normalized });
                      }}
                      placeholder="e.g. hospital.example.com (hostname only; no https://)"
                      style={{ textTransform: 'lowercase' }}
                      disabled={!isEditMode}
                    />
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Optional. Hostname only. When set, users visiting this hostname may see the tenant&apos;s custom login UI.
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setValidationErrors({ ...validationErrors, email: '' });
                    }}
                    placeholder="tenant@example.com"
                    className={validationErrors.email ? 'input-error' : ''}
                    disabled={!isEditMode}
                  />
                  {validationErrors.email && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.email}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, phone: numeric });
                      setValidationErrors({ ...validationErrors, phone: '' });
                    }}
                    placeholder="1234567890"
                    className={validationErrors.phone ? 'input-error' : ''}
                    maxLength={10}
                    disabled={!isEditMode}
                  />
                  {validationErrors.phone && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.phone}
                    </small>
                  )}
                  {!validationErrors.phone && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      10 digits without spaces or special characters
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value });
                        setValidationErrors({ ...validationErrors, password: '' });
                      }}
                      placeholder="Leave blank to keep current password"
                      className={validationErrors.password ? 'input-error' : ''}
                      style={{ paddingRight: '45px' }}
                      disabled={!isEditMode}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '5px',
                        color: '#666',
                        fontSize: '20px',
                      }}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.password}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Leave blank to keep current password. Minimum 8 characters if changing.
                  </small>
                </div>

                <div className="form-group">
                  <label>Tenant Admin MPIN</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showMpin ? 'text' : 'password'}
                      value={formData.mPin}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setFormData({ ...formData, mPin: digits });
                        setValidationErrors({ ...validationErrors, mPin: '' });
                      }}
                      placeholder="Leave blank to keep current MPIN"
                      className={validationErrors.mPin ? 'input-error' : ''}
                      disabled={!isEditMode}
                      style={{ paddingRight: '45px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowMpin(!showMpin)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '5px',
                        color: '#666',
                        fontSize: '20px',
                      }}
                      disabled={!isEditMode}
                    >
                      {showMpin ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.mPin && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.mPin}
                    </small>
                  )}
                  {!validationErrors.mPin && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Leave blank to keep current MPIN. 4–6 digit numeric characters if changing.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit' }}
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>Group ID (GPS)</label>
                  <input
                    type="text"
                    value={formData.groupId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        groupId: e.target.value.replace(/\D/g, ''),
                      })
                    }
                    placeholder="GPS group ID for all ambulances/vehicles"
                    disabled={!isEditMode || !isDevMode}
                  />
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    All ambulances/vehicles under this tenant will use this group ID
                  </small>
                </div>

                {/* Hospital Details Section */}
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e2e8f0' }}>
                  <h3 style={{ marginBottom: '24px' }}>Hospital Details</h3>

                  <div className="form-group">
                    <label>
                      Hospital Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.hospital.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          hospital: { ...formData.hospital, name: e.target.value },
                        });
                        setValidationErrors({ ...validationErrors, hospitalName: '' });
                      }}
                      placeholder="Hospital name"
                      className={validationErrors.hospitalName ? 'input-error' : ''}
                      disabled={!isEditMode}
                    />
                    {validationErrors.hospitalName && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.hospitalName}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Logo</label>
                    <div className="avatar-upload-row">
                      <input
                        type="url"
                        value={formData.hospital.logo}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, logo: e.target.value },
                          })
                        }
                        placeholder="https://example.com/logo.png"
                        style={{ flex: '1 1 260px' }}
                        disabled={!isEditMode}
                      />
                      {!formData.hospital.logo && isEditMode && (
                        <label className={`avatar-upload-button ${logoUploading ? 'disabled' : ''}`} style={{ marginBottom: 0 }}>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleLogoFileChange}
                            disabled={logoUploading || !isEditMode}
                          />
                          {logoUploading ? (
                            <>
                              <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <span role="img" aria-label="upload" style={{ marginRight: '8px', display: 'inline-block' }}>
                                📤
                              </span>
                              Upload
                            </>
                          )}
                        </label>
                      )}
                      {formData.hospital.logo && isEditMode && (
                        <button type="button" onClick={handleClearLogo} className="btn-secondary avatar-clear-button">
                          Clear
                        </button>
                      )}
                    </div>
                    {logoUploading && (
                      <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        Processing & uploading logo...
                      </small>
                    )}
                    {logoUploadError && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {logoUploadError}
                      </small>
                    )}
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Upload an image or paste a URL for the hospital logo. <span style={{ fontWeight: 600, color: '#374151' }}>Max size: 1.5MB.</span>
                    </small>
                    {formData.hospital.logo && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: 120,
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            aspectRatio: '1 / 1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={formData.hospital.logo}
                            alt="Hospital logo preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </div>
                        <span className="avatar-preview-text">{formData.hospital.logo}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Hospital Address Details</label>
                    <textarea
                      value={formData.hospital.addressDetails}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hospital: { ...formData.hospital, addressDetails: e.target.value },
                        })
                      }
                      placeholder="Flat/area/landmark (combined with detected address)"
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                      disabled={!isEditMode}
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>
                      Hospital Location <span className="required-star">*</span>
                    </label>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                      Search for a place or click on the map to set the hospital location
                    </p>
                    <PlaceSearch
                      onPlaceSelect={isEditMode ? (lat, lng, address) => {
                        handleLocationChange(lat, lng, address);
                      } : () => { }}
                      placeholder="Search for hospital location..."
                      currentLocation={hospitalLocation}
                      disabled={!isEditMode}
                    />
                    <div style={{ pointerEvents: isEditMode ? 'auto' : 'none', opacity: isEditMode ? 1 : 0.6 }}>
                      <GoogleMap
                        center={hospitalLocation ? { lat: hospitalLocation.latitude, lng: hospitalLocation.longitude } : { lat: 28.6139, lng: 77.2090 }}
                        zoom={hospitalLocation ? 13 : 10}
                        location={hospitalLocation}
                        onLocationChange={isEditMode ? (lat, lng) => {
                          handleLocationChange(lat, lng);
                        } : undefined}
                        height="300px"
                        showMapTypeControl={true}
                      />
                    </div>
                    {hospitalLocation && (
                      <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                        <strong>Selected:</strong> {hospitalLocation.latitude.toFixed(6)}, {hospitalLocation.longitude.toFixed(6)}
                      </div>
                    )}
                    {validationErrors.hospitalLocation && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.hospitalLocation}
                      </small>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={formData.hospital.city}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, city: e.target.value },
                          })
                        }
                        placeholder="City"
                        disabled={!isEditMode}
                      />
                    </div>

                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={formData.hospital.state}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, state: e.target.value },
                          })
                        }
                        placeholder="State"
                        disabled={!isEditMode}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Zip Code</label>
                      <input
                        type="text"
                        value={formData.hospital.zipCode}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, zipCode: e.target.value },
                          })
                        }
                        placeholder="Zip code"
                        disabled={!isEditMode}
                      />
                    </div>

                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        value={formData.hospital.country}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, country: e.target.value },
                          })
                        }
                        placeholder="Country"
                        disabled={!isEditMode}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Hospital Phone</label>
                      <input
                        type="tel"
                        value={formData.hospital.phone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, phone: e.target.value.replace(/\D/g, '').slice(0, 10) },
                          })
                        }
                        placeholder="Hospital phone"
                        maxLength={10}
                        disabled={!isEditMode}
                      />
                    </div>

                    <div className="form-group">
                      <label>Hospital Email</label>
                      <input
                        type="email"
                        value={formData.hospital.email}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, email: e.target.value },
                          });
                          setValidationErrors({ ...validationErrors, hospitalEmail: '' });
                        }}
                        placeholder="hospital@example.com"
                        className={validationErrors.hospitalEmail ? 'input-error' : ''}
                        disabled={!isEditMode}
                      />
                      {validationErrors.hospitalEmail && (
                        <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                          {validationErrors.hospitalEmail}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      value={formData.hospital.website}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hospital: { ...formData.hospital, website: e.target.value },
                        })
                      }
                      placeholder="https://example.com"
                      disabled={!isEditMode}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Registration Number</label>
                      <input
                        type="text"
                        value={formData.hospital.registrationNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, registrationNumber: e.target.value },
                          })
                        }
                        placeholder="Registration number"
                        disabled={!isEditMode}
                      />
                    </div>

                    <div className="form-group">
                      <label>License Number</label>
                      <input
                        type="text"
                        value={formData.hospital.licenseNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, licenseNumber: e.target.value },
                          })
                        }
                        placeholder="License number"
                        disabled={!isEditMode}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Emergency Contact</label>
                      <input
                        type="text"
                        value={formData.hospital.emergencyContact}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, emergencyContact: e.target.value },
                          })
                        }
                        placeholder="Emergency contact name"
                        disabled={!isEditMode}
                      />
                    </div>

                    <div className="form-group">
                      <label>Emergency Phone</label>
                      <input
                        type="tel"
                        value={formData.hospital.emergencyPhone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, emergencyPhone: e.target.value.replace(/\D/g, '').slice(0, 10) },
                          })
                        }
                        placeholder="Emergency phone"
                        maxLength={10}
                        disabled={!isEditMode}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Footer with Save Button */}
              <div style={{ flexShrink: 0, marginBottom: '-15px', marginTop: '15px', paddingTop: '16px', paddingBottom: '8px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
                {!isEditMode ? (
                  canModifyTenantSettings ? (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="btn-submit"
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  ) : null
                ) : (
                  <>
                    {hasChanges() && (
                      <span
                        style={{
                          fontSize: '14px',
                          color: '#f59e0b',
                          fontWeight: '500',
                          marginRight: '8px',
                        }}
                      >
                        Have Edit Changes
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn-secondary"
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginTop: '0px',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={saving || !hasChanges()}
                      style={{
                        opacity: hasChanges() ? 1 : 0.6,
                        cursor: hasChanges() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="loading-spinner btn-spinner" aria-hidden />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default TenantSettings;

